import * as React from "react";
import { toast } from "react-toastify";

import {
  CreateSnapshotExportPayload,
  SnapshotExport,
  useCreateSnapshotExportMutation,
  useGetSnapshotExportQuery,
} from "@/modules/snapshots/businessReports.api";

const SNAPSHOT_EXPORT_POLLING_INTERVAL_MS = 3000;

export const getSnapshotExportDownloadUrl = (snapshotExportId: string): string =>
  `/snapshot-exports/${snapshotExportId}/download`;

const isTerminalExport = (snapshotExport: SnapshotExport): boolean =>
  ["COMPLETED", "FAILED", "CANCELLED"].includes(snapshotExport.status);

const getApiErrorMessage = (error: unknown): string | undefined => {
  const apiError = error as { data?: { detail?: string } };
  return apiError?.data?.detail;
};

export const useSnapshotExport = () => {
  const [activeExportId, setActiveExportId] = React.useState<string | undefined>();
  const [createSnapshotExport, createSnapshotExportState] = useCreateSnapshotExportMutation();
  const snapshotExportQuery = useGetSnapshotExportQuery(activeExportId ?? "", {
    skip: !activeExportId,
    pollingInterval: activeExportId ? SNAPSHOT_EXPORT_POLLING_INTERVAL_MS : 0,
    skipPollingIfUnfocused: true,
  });

  const startSnapshotExport = React.useCallback(
    async (payload: CreateSnapshotExportPayload) => {
      try {
        const snapshotExport = await createSnapshotExport(payload).unwrap();
        setActiveExportId(snapshotExport.id);
        toast.info("Snapshot export is being prepared");
      } catch (error) {
        toast.error(getApiErrorMessage(error) ?? "Unable to start snapshot export");
      }
    },
    [createSnapshotExport],
  );

  React.useEffect(() => {
    const snapshotExport = snapshotExportQuery.data;
    if (!activeExportId) return;

    if (snapshotExportQuery.isError) {
      toast.error("Unable to check snapshot export status");
      setActiveExportId(undefined);
      return;
    }

    if (!snapshotExport || !isTerminalExport(snapshotExport)) return;

    if (snapshotExport.status === "COMPLETED" && snapshotExport.download_available) {
      toast.success("Snapshot export is ready");
      document.location.href = getSnapshotExportDownloadUrl(snapshotExport.id);
    } else if (snapshotExport.status === "COMPLETED") {
      toast.error("Snapshot export finished without a downloadable file");
    } else {
      toast.error(snapshotExport.error_message ?? "Snapshot export failed");
    }

    setActiveExportId(undefined);
  }, [activeExportId, snapshotExportQuery.data, snapshotExportQuery.isError]);

  return {
    activeExport: snapshotExportQuery.data,
    isExporting: createSnapshotExportState.isLoading || !!activeExportId,
    startSnapshotExport,
  };
};
